import React, { useState, useEffect } from 'react';
import { Form, Button, Spinner, Alert } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { resetPassword, clearError, resetAuthStatus } from '../../store/slices/authSlice';
import AuthLayout from './AuthLayout';

const ResetPassword = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  
  const { loading, error, success } = useSelector((state) => state.auth);
  
  // email and otp passed from VerifyOTP page
  const email = location.state?.email || '';
  const otp = location.state?.otp || '';

  useEffect(() => {
    dispatch(resetAuthStatus());
    if (!email || !otp) {
      navigate('/forgot-password');
    }
  }, [email, otp, navigate, dispatch]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (password === confirmPassword) {
      dispatch(resetPassword({ email, otp, password }));
    }
  };

  const isFormValid = password && password === confirmPassword;

  if (success) {
    return (
      <AuthLayout showBrand={false}>
        <div className="text-center mb-4">
          <div className="icon-wrapper text-center mb-3">
            <div className="icon bg-success bg-opacity-25 text-success d-flex align-items-center justify-content-center rounded-circle mx-auto" style={{ width: "60px", height: "60px" }}>
              <i className="bi bi-patch-check-fill fs-3"></i>
            </div>
          </div>
          <div className="main-heading mb-2">Password Reset Successfully!</div>
          <div className="small text-muted">Your password has been reset successfully.<br /> Redirecting you to login...</div>
          <Button 
            variant="primary" 
            className="mt-4 w-100 btn-custom"
            onClick={() => navigate('/login')}
          >
            Go to Login
          </Button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Reset Password"
      subtitle="Create a strong password for your account"
    >
      {error && <Alert variant="danger" dismissible onClose={() => dispatch(clearError())}>{error}</Alert>}
      
      <Form onSubmit={handleSubmit}>
        <Form.Group className="mb-3" controlId="resetPassword">
          <Form.Label>New Password</Form.Label>
          <div className="password-icon-wrapper position-relative">
            <Form.Control
              type={showPassword ? 'text' : 'password'}
              placeholder="Enter new password"
              value={password}
              maxLength={128}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={loading}
            />
            <span 
              className="eye-btn position-absolute end-0 top-50 translate-middle-y me-3 pointer" 
              onClick={() => setShowPassword(!showPassword)}
              style={{ zIndex: 10, cursor: 'pointer' }}
            >
              {showPassword ? <i className="bi bi-eye-slash"></i> : <i className="bi bi-eye"></i>}
            </span>
          </div>
        </Form.Group>

        <Form.Group className="mb-3" controlId="resetConfirmPassword">
          <Form.Label>Confirm Password</Form.Label>
          <div className="password-icon-wrapper position-relative">
            <Form.Control
              type={showConfirmPassword ? 'text' : 'password'}
              placeholder="Confirm your password"
              value={confirmPassword}
              maxLength={128}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              disabled={loading}
            />
            <span 
              className="eye-btn position-absolute end-0 top-50 translate-middle-y me-3 pointer" 
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              style={{ zIndex: 10, cursor: 'pointer' }}
            >
              {showConfirmPassword ? <i className="bi bi-eye-slash"></i> : <i className="bi bi-eye"></i>}
            </span>
          </div>
        </Form.Group>
        
        <div className="btn-wrapper mt-4">
          <Button 
            variant="primary" 
            type="submit" 
            className="w-100 btn-custom d-flex align-items-center justify-content-center gap-2" 
            disabled={!isFormValid || loading}
          >
            {loading ? (
              <>
                <Spinner animation="border" size="sm" />
                Resetting Password...
              </>
            ) : 'Reset Password'}
          </Button>
        </div>
      </Form>
    </AuthLayout>
  );
};

export default ResetPassword;
